// MARK://dhk8/pos/lib/clients/kds
// Typed client for dhk8-kds-tickets. Reconciled against the contract at
// services/dhk8-kds-tickets/src/contract.ts (commit 2ffb5cb), absorbed from the
// former standalone `apps/dhk8-kds`.
//
// Routes: `${API_BASE}/api/v1/kds/tickets/...`. Envelope: { ok, data } | bare T.
// State machine: PENDING ─start→ IN_PROGRESS ─ready→ READY ─bump→ BUMPED ─recall→ RECALLED.
//   start: PENDING|RECALLED → IN_PROGRESS · ready: PENDING|IN_PROGRESS|RECALLED → READY
//   bump:  IN_PROGRESS|READY → BUMPED (leaves the board) · recall: BUMPED → RECALLED (returns)
// Board: GET .../kds/tickets?stationId=…&active=true → on-board tickets (everything
//   except BUMPED: PENDING|IN_PROGRESS|READY|RECALLED), priority desc, firedAt asc.
//   The board client-side splits them into Nuevos / En preparación / Listos.
//
// The real `dhk8-kds-tickets` endpoints have shipped (services/dhk8-kds-tickets/
// src/api/routes.ts: GET /v1/kds/tickets, GET /:id, POST /:id/{start,ready,bump,
// recall}, PATCH /:id/priority, PATCH /:id/items/:itemId/status — backed by a
// real Prisma repository + service + Fastify server). This client targets them by
// default via the `real*` branch below.
//
// STUB MODE (dev-only): when `import.meta.env.DEV` or VITE_KDS_STUB=1 we serve
// deterministic seed tickets so cashier/cocina can practice the flow offline /
// before the org's backend is reachable. Set VITE_KDS_STUB=0 to hit the real API
// in dev. `kds-client.test.ts` pins the stub semantics.
//
// TODO backend: dhk8-kds-stations list — StationPicker is free-text until then.

import { gqlClient } from '../gql.js'
import {
  PosKitchenTicketsQuery, PosKitchenTicketQuery,
  PosKdsStartTicketMutation, PosKdsReadyTicketMutation, PosKdsBumpTicketMutation,
  PosKdsRecallTicketMutation, PosKdsSetPriorityMutation, PosKdsSetItemStatusMutation,
} from '../queries/kds.queries.js'
import type {
  KitchenTicketDto,
  KitchenTicketItemDto,
  KitchenTicketStatus,
  KitchenTicketItemStatus,
} from '@dockethub/ui-restaurant'

export type { KitchenTicketDto, KitchenTicketItemDto, KitchenTicketStatus, KitchenTicketItemStatus } from '@dockethub/ui-restaurant'
export type TicketStatus = KitchenTicketStatus
export type TicketItemStatus = KitchenTicketItemStatus


function stubEnabled(): boolean {
  const flag = import.meta.env.VITE_KDS_STUB as string | undefined
  if (flag === '1' || flag === 'true') return true
  if (flag === '0' || flag === 'false') return false
  return !!import.meta.env.DEV
}

// ── envelope unwrap ─────────────────────────────────────────────────────────


interface Paged<T> { items: T[]; total: number; page: number; limit: number }

export interface ListTicketsQuery {
  tenantId?: string
  branchId?: string
  stationId?: string
  orderId?: string
  status?: TicketStatus
  active?: boolean
  page?: number
  limit?: number
}

type Verb = 'start' | 'ready' | 'bump' | 'recall'

// ── real client ─────────────────────────────────────────────────────────────

async function realListTickets(query: ListTicketsQuery, signal?: AbortSignal): Promise<KitchenTicketDto[]> {
  // F4.3 / ADR-0009: lectura por el grafo (kds-tickets, tenant del JWT). Acciones REST.
  // requestPolicy network-only: el KDS hace polling cada POLL_MS vía react-query,
  // pero urql es cache-first por default → servía el MISMO snapshot cacheado y la
  // pizarra nunca cambiaba (tickets nuevos no aparecían, bumped no desaparecían).
  // Forzamos red en cada poll para que el board refleje el estado real de cocina.
  const ctx = { requestPolicy: 'network-only' as const, ...(signal ? { fetchOptions: { signal } } : {}) }
  const vars: { branchId?: string; stationId?: string; orderId?: string; status?: TicketStatus; active?: boolean; page?: number; limit?: number } = {}
  if (query.branchId) vars.branchId = query.branchId
  if (query.stationId) vars.stationId = query.stationId
  if (query.orderId) vars.orderId = query.orderId
  if (query.status) vars.status = query.status
  if (query.active) vars.active = true
  if (query.page) vars.page = query.page
  if (query.limit) vars.limit = query.limit
  const res = await gqlClient.query(PosKitchenTicketsQuery, vars, ctx).toPromise()
  if (res.error) throw res.error
  return (res.data?.kitchenTickets?.items ?? []) as unknown as KitchenTicketDto[]
}
// F8.1 / ADR-0009: acciones del KDS por el grafo (mutation, tenant del JWT + RLS;
// el subgraph envuelve withTenantContext → RLS aísla por tenant, IDOR-safe).
const KDS_ACTION_DOC = {
  start: PosKdsStartTicketMutation, ready: PosKdsReadyTicketMutation,
  bump: PosKdsBumpTicketMutation, recall: PosKdsRecallTicketMutation,
} as const
const KDS_ACTION_FIELD: Record<Verb, string> = {
  start: 'startKitchenTicket', ready: 'readyKitchenTicket',
  bump: 'bumpKitchenTicket', recall: 'recallKitchenTicket',
}
async function realAction(id: string, verb: Verb): Promise<KitchenTicketDto> {
  const res = await gqlClient.mutation(KDS_ACTION_DOC[verb], { id }).toPromise()
  if (res.error) throw res.error
  const t = (res.data as Record<string, KitchenTicketDto | null> | undefined)?.[KDS_ACTION_FIELD[verb]]
  if (!t) throw new Error(`NOT_FOUND: kitchen ticket ${id}`)
  return t as unknown as KitchenTicketDto
}
async function realGet(id: string, signal?: AbortSignal): Promise<KitchenTicketDto> {
  const ctx = { requestPolicy: 'network-only' as const, ...(signal ? { fetchOptions: { signal } } : {}) }
  const res = await gqlClient.query(PosKitchenTicketQuery, { id }, ctx).toPromise()
  if (res.error) throw res.error
  const t = res.data?.kitchenTicket
  if (!t) throw new Error(`NOT_FOUND: kitchen ticket ${id}`)
  return t as unknown as KitchenTicketDto
}
async function realSetPriority(id: string, priority: number): Promise<KitchenTicketDto> {
  const res = await gqlClient.mutation(PosKdsSetPriorityMutation, { id, priority }).toPromise()
  if (res.error) throw res.error
  const t = res.data?.setKitchenTicketPriority
  if (!t) throw new Error(`NOT_FOUND: kitchen ticket ${id}`)
  return t as unknown as KitchenTicketDto
}
async function realSetItemStatus(id: string, itemId: string, status: TicketItemStatus): Promise<KitchenTicketDto> {
  const res = await gqlClient.mutation(PosKdsSetItemStatusMutation, { ticketId: id, itemId, status }).toPromise()
  if (res.error) throw res.error
  const t = res.data?.updateKitchenTicketItemStatus
  if (!t) throw new Error(`NOT_FOUND: kitchen ticket ${id}`)
  return t as unknown as KitchenTicketDto
}

// ── stub client (dev) ───────────────────────────────────────────────────────

let stub: KitchenTicketDto[] | null = null
function seed(): KitchenTicketDto[] {
  const now = Date.now()
  const iso = (off: number) => new Date(now - off).toISOString()
  return [
    {
      id: 'tk_001', tenantId: 't0', branchId: 'b1', orderId: 'ord_101', stationId: 'parrilla',
      ticketNumber: 101, status: 'PENDING', priority: 1, firedAt: iso(2 * 60_000),
      startedAt: null, readyAt: null, bumpedAt: null, recalledAt: null, notes: null,
      items: [
        { id: 'it_1', orderItemId: 'oi_1', name: 'Taco al pastor', quantity: 3, modifiers: [], notes: null, status: 'PENDING' },
        { id: 'it_2', orderItemId: 'oi_2', name: 'Cerveza', quantity: 2, modifiers: [], notes: null, status: 'PENDING' },
      ],
    },
    {
      id: 'tk_002', tenantId: 't0', branchId: 'b1', orderId: 'ord_102', stationId: 'parrilla',
      ticketNumber: 102, status: 'IN_PROGRESS', priority: 0, firedAt: iso(6 * 60_000),
      startedAt: iso(4 * 60_000), readyAt: null, bumpedAt: null, recalledAt: null, notes: 'Sin cebolla',
      items: [
        { id: 'it_3', orderItemId: 'oi_3', name: 'Arrachera', quantity: 1, modifiers: [], notes: 'Término medio', status: 'PREPARING' },
        { id: 'it_4', orderItemId: 'oi_4', name: 'Guacamole', quantity: 1, modifiers: [], notes: null, status: 'PENDING' },
      ],
    },
    {
      id: 'tk_003', tenantId: 't0', branchId: 'b1', orderId: 'ord_103', stationId: 'frios',
      ticketNumber: 103, status: 'READY', priority: 0, firedAt: iso(10 * 60_000),
      startedAt: iso(8 * 60_000), readyAt: iso(60_000), bumpedAt: null, recalledAt: null, notes: null,
      items: [{ id: 'it_5', orderItemId: 'oi_5', name: 'Burrito', quantity: 2, modifiers: [], notes: null, status: 'READY' }],
    },
  ]
}
function stubAll(): KitchenTicketDto[] { if (!stub) stub = seed(); return stub }
// "active" / "live" for a KDS board = still on the board, i.e. not BUMPED.
// READY tickets stay (they fill the "Listos" column) until they're bumped.
function stubList(query: ListTicketsQuery): KitchenTicketDto[] {
  let list = stubAll()
  if (query.stationId) list = list.filter((t) => t.stationId === query.stationId)
  if (query.status) list = list.filter((t) => t.status === query.status)
  if (query.active) list = list.filter((t) => t.status !== 'BUMPED')
  else list = list.filter((t) => t.status !== 'BUMPED')
  return list
}
const NEXT: Record<Verb, TicketStatus> = { start: 'IN_PROGRESS', ready: 'READY', bump: 'BUMPED', recall: 'RECALLED' }
function stubAction(id: string, verb: Verb): KitchenTicketDto {
  const list = stubAll()
  const cur = list.find((x) => x.id === id)
  if (!cur) throw new Error(`stub: ticket ${id} not found`)
  const next: KitchenTicketDto = { ...cur, status: NEXT[verb] }
  const stamp = new Date().toISOString()
  if (verb === 'start') next.startedAt = stamp
  if (verb === 'ready') next.readyAt = stamp
  if (verb === 'bump') next.bumpedAt = stamp
  if (verb === 'recall') next.recalledAt = stamp
  stub = list.map((x) => (x.id === id ? next : x))
  return next
}

// ── public API (real-or-stub) ───────────────────────────────────────────────

export async function listTickets(query: ListTicketsQuery = {}, signal?: AbortSignal): Promise<KitchenTicketDto[]> {
  if (stubEnabled()) return Promise.resolve(stubList(query))
  return realListTickets(query, signal)
}
export async function getTicket(id: string, signal?: AbortSignal): Promise<KitchenTicketDto> {
  if (stubEnabled()) { const t = stubAll().find((x) => x.id === id); if (!t) throw new Error(`stub: ${id}`); return Promise.resolve(t) }
  return realGet(id, signal)
}
function action(id: string, verb: Verb): Promise<KitchenTicketDto> {
  if (stubEnabled()) return Promise.resolve(stubAction(id, verb))
  return realAction(id, verb)
}
export const startTicket = (id: string) => action(id, 'start')
export const readyTicket = (id: string) => action(id, 'ready')
export const bumpTicket = (id: string) => action(id, 'bump')
export const recallTicket = (id: string) => action(id, 'recall')

export async function setTicketPriority(id: string, priority: number): Promise<KitchenTicketDto> {
  if (stubEnabled()) {
    const list = stubAll()
    const cur = list.find((x) => x.id === id)
    if (!cur) throw new Error(`stub: ${id}`)
    const next = { ...cur, priority }
    stub = list.map((x) => (x.id === id ? next : x))
    return Promise.resolve(next)
  }
  return realSetPriority(id, priority)
}
export async function updateTicketItemStatus(id: string, itemId: string, status: TicketItemStatus): Promise<KitchenTicketDto> {
  if (stubEnabled()) {
    const list = stubAll()
    const cur = list.find((x) => x.id === id)
    if (!cur) throw new Error(`stub: ${id}`)
    const next = { ...cur, items: cur.items.map((it) => (it.id === itemId ? { ...it, status } : it)) }
    stub = list.map((x) => (x.id === id ? next : x))
    return Promise.resolve(next)
  }
  return realSetItemStatus(id, itemId, status)
}

// ── test seam ───────────────────────────────────────────────────────────────
/** Reset the in-memory stub (test-only). */
export function __resetStub(): void { stub = null }

// ── legacy shim (kept so the prior KdsPage import path keeps compiling during
//    the swap; new code should use the verb helpers above). ──────────────────
/** @deprecated use startTicket/readyTicket/bumpTicket/recallTicket */
export async function setTicketStatus(id: string, next: KitchenTicketStatus): Promise<KitchenTicketDto | null> {
  const verb: Verb | null =
    next === 'IN_PROGRESS' ? 'start' : next === 'READY' ? 'ready' : next === 'BUMPED' ? 'bump' : next === 'RECALLED' ? 'recall' : null
  if (!verb) return null
  try { return await action(id, verb) } catch { return null }
}
